import { mapShift, type ShiftID } from './dashboard.models';

// Mirrors Sierra.Production.Models.Enum.KnittingMachineStatus (order = numeric value)
export const KNITTING_MACHINE_STATUS_VALUES = [
  'Idle',
  'Loading',
  'Unloading',
  'InUse',
  'Available',
  'OutOfService',
  'Maintenance',
] as const;

export type KnittingMachineStatus = typeof KNITTING_MACHINE_STATUS_VALUES[number];

export interface MachineProductionRate {
  assetId: string;
  machineCode: string;
  gauge: number | null;
  status: KnittingMachineStatus | null;
  lbsProducedInShift: number;
  hoursElapsedInShift: number;
  lbsPerHour: number;
  rollCount: number;
}

export interface ShiftProductionRate {
  shift: ShiftID;
  shiftStartUtc: string;
  shiftEndUtc: string;
  machines: MachineProductionRate[];
}

// Raw shape over the wire — enums arrive as numbers (default System.Text.Json behavior)
interface RawMachineProductionRate extends Omit<MachineProductionRate, 'status'> {
  status: number | KnittingMachineStatus | null;
}

interface RawShiftProductionRate extends Omit<ShiftProductionRate, 'shift' | 'machines'> {
  shift: number | ShiftID;
  machines: RawMachineProductionRate[];
}

function mapStatus(raw: number | KnittingMachineStatus | null | undefined): KnittingMachineStatus | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return KNITTING_MACHINE_STATUS_VALUES[raw] ?? null;
  return KNITTING_MACHINE_STATUS_VALUES.includes(raw) ? raw : null;
}

export function normalizeShiftProductionRate(raw: RawShiftProductionRate): ShiftProductionRate {
  return {
    shift: mapShift(raw.shift),
    shiftStartUtc: raw.shiftStartUtc,
    shiftEndUtc: raw.shiftEndUtc,
    machines: raw.machines.map(m => ({
      assetId: m.assetId,
      machineCode: m.machineCode,
      gauge: m.gauge,
      status: mapStatus(m.status),
      lbsProducedInShift: m.lbsProducedInShift,
      hoursElapsedInShift: m.hoursElapsedInShift,
      lbsPerHour: m.lbsPerHour,
      rollCount: m.rollCount,
    })),
  };
}
